import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import {
  FileText, CheckCircle, Clock, AlertTriangle,
  Download, Eye, PenTool, Calendar, Loader2, X, RotateCcw
} from 'lucide-react';
import './LegalVault.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LegalVault = () => {
  const [agreements, setAgreements] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [viewingPdf, setViewingPdf] = useState(null);
  const [signingDoc, setSigningDoc] = useState(null);
  const [legalName, setLegalName] = useState('');
  const [signing, setSigning] = useState(false);
  const sigPadRef = useRef(null);
  const sigPadWrapperRef = useRef(null);
  const [sigPadWidth, setSigPadWidth] = useState(400);

  // Existing agreement state
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [signingAgreement, setSigning2] = useState(false);
  const [signatureName, setSignatureName] = useState('');

  const getToken = () => localStorage.getItem('artistToken');
  const getHeaders = () => ({
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    fetchAll();
  }, []);

  // ResizeObserver for dynamic signature canvas sizing
  useEffect(() => {
    if (!signingDoc || !sigPadWrapperRef.current) return;
    const el = sigPadWrapperRef.current;
    const measure = () => {
      const w = el.offsetWidth;
      setSigPadWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [signingDoc]);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchAgreements(), fetchDocuments()]);
    setLoading(false);
  };

  const fetchAgreements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/artist/agreements`, { headers: getHeaders() });
      if (res.ok) setAgreements(await res.json());
    } catch (e) { /* silent */ }
  };

  const fetchDocuments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/artist/documents`, { headers: getHeaders() });
      if (res.ok) setDocuments(await res.json());
    } catch (e) { /* silent */ }
  };

  const handleViewPdf = async (docId, isSigned = false) => {
    const endpoint = isSigned
      ? `${API_URL}/api/artist/documents/${docId}/signed-pdf`
      : `${API_URL}/api/artist/documents/${docId}/pdf`;
    try {
      const res = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setViewingPdf(url);
      } else {
        toast.error('Failed to load document');
      }
    } catch {
      toast.error('Failed to load document');
    }
  };

  const handleSign = async () => {
    if (!legalName.trim()) {
      toast.error('Please enter your legal name');
      return;
    }
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      toast.error('Please draw your signature');
      return;
    }

    setSigning(true);
    try {
      const signatureData = sigPadRef.current.toDataURL('image/png');
      // Strip the data:image/png;base64, prefix
      const signatureImage = signatureData.replace('data:image/png;base64,', '');

      const res = await fetch(`${API_URL}/api/artist/documents/${signingDoc.id}/sign`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          signature_image: signatureImage,
          legal_name: legalName.trim(),
        }),
      });

      if (res.ok) {
        const result = await res.json();
        toast.success('Document signed successfully!');
        setSigningDoc(null);
        setLegalName('');
        fetchDocuments();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to sign document');
      }
    } catch {
      toast.error('Failed to sign document');
    } finally {
      setSigning(false);
    }
  };

  const handleSignAgreement = async (agreementId) => {
    if (!signatureName.trim()) {
      toast.error('Please enter your legal name to sign');
      return;
    }
    setSigning2(true);
    try {
      const res = await fetch(
        `${API_URL}/api/artist/agreements/${agreementId}/sign?signature_name=${encodeURIComponent(signatureName)}`,
        { method: 'POST', headers: getHeaders() }
      );
      if (res.ok) {
        toast.success('Agreement signed successfully!');
        fetchAgreements();
        setSelectedAgreement(null);
        setSignatureName('');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to sign agreement');
      }
    } catch {
      toast.error('Failed to sign agreement');
    } finally {
      setSigning2(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="legal-vault loading">
        <Loader2 size={32} className="spinner" />
        <p>Loading your documents...</p>
      </div>
    );
  }

  // ── PDF Viewer Modal ──
  if (viewingPdf) {
    return (
      <div className="legal-vault">
        <div className="pdf-viewer-header">
          <button className="close-viewer" onClick={() => { setViewingPdf(null); URL.revokeObjectURL(viewingPdf); }} data-testid="close-pdf">
            <X size={20} /> Close
          </button>
        </div>
        <iframe src={viewingPdf} className="pdf-viewer-frame" title="Document PDF" data-testid="pdf-viewer" />
      </div>
    );
  }

  // ── Signing Modal — rendered as overlay in main return ──

  // ── Main View ──
  return (
    <div className="legal-vault" data-testid="legal-vault">
      <div className="vault-header">
        <FileText size={28} />
        <div>
          <h2>Legal Vault</h2>
          <p>View, sign, and download your documents and agreements</p>
        </div>
      </div>

      {/* Documents Section */}
      <div className="vault-section" data-testid="documents-section">
        <h3 className="section-label">Documents</h3>
        {documents.length === 0 ? (
          <div className="vault-empty">
            <p>No documents yet. When AMR sends you documents, they'll appear here.</p>
          </div>
        ) : (
          <div className="documents-list">
            {documents.map(doc => (
              <div key={doc.id} className="document-card" data-testid={`doc-${doc.id}`}>
                <div className="document-main">
                  <div className="document-icon">
                    {doc.status === 'signed' ? <CheckCircle size={20} /> : <FileText size={20} />}
                  </div>
                  <div className="document-info">
                    <h4>{doc.title}</h4>
                    {doc.description && <p className="doc-desc">{doc.description}</p>}
                    <div className="doc-meta">
                      <span className={`doc-type-badge ${doc.document_type}`}>{doc.document_type}</span>
                      <span className="doc-date"><Calendar size={12} /> {formatDate(doc.sent_at)}</span>
                    </div>
                  </div>
                  <span className={`doc-status ${doc.status}`}>
                    {doc.status === 'sent' && <Clock size={14} />}
                    {doc.status === 'viewed' && <Eye size={14} />}
                    {doc.status === 'signed' && <CheckCircle size={14} />}
                    {doc.status === 'artist_signed' && <PenTool size={14} />}
                    {doc.status === 'completed' && <CheckCircle size={14} />}
                    {doc.status === 'artist_signed' ? 'Awaiting Counter-Signature' : doc.status === 'completed' ? 'Fully Executed' : doc.status}
                  </span>
                </div>

                <div className="document-actions">
                  <button className="action-btn-lv view" onClick={() => handleViewPdf(doc.id)} data-testid={`view-${doc.id}`}>
                    <Eye size={14} /> View PDF
                  </button>

                  {doc.status !== 'signed' && doc.status !== 'artist_signed' && doc.status !== 'completed' && doc.requires_signature && (
                    <button className="action-btn-lv sign" onClick={() => setSigningDoc(doc)} data-testid={`sign-${doc.id}`}>
                      <PenTool size={14} /> Sign Document
                    </button>
                  )}

                  {(doc.status === 'signed' || doc.status === 'artist_signed' || doc.status === 'completed') && (
                    <>
                      <button className="action-btn-lv view" onClick={() => handleViewPdf(doc.id, true)}>
                        <Download size={14} /> View Signed Copy
                      </button>
                      <div className="signed-meta">
                        <CheckCircle size={12} />
                        Signed by {doc.signer_legal_name} on {formatDate(doc.signed_at)}
                        {doc.status === 'completed' && doc.countersigner_name && (
                          <> &mdash; Counter-signed by {doc.countersigner_name}</>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Legacy Agreements Section */}
      {agreements.length > 0 && (
        <div className="vault-section">
          <h3 className="section-label">Agreements</h3>
          <div className="agreements-list">
            {agreements.map((agreement) => (
              <div key={agreement.id} className={`agreement-card ${selectedAgreement?.id === agreement.id ? 'expanded' : ''}`}>
                <div className="agreement-main" onClick={() => setSelectedAgreement(selectedAgreement?.id === agreement.id ? null : agreement)}>
                  <div className="agreement-icon">
                    {agreement.status === 'signed' ? <CheckCircle size={18} /> : agreement.status === 'pending' ? <Clock size={18} /> : <AlertTriangle size={18} />}
                  </div>
                  <div className="agreement-info">
                    <h3>{agreement.agreement_title}</h3>
                    <p className="agreement-version">Version {agreement.agreement_version}</p>
                  </div>
                  <span className={`status-badge-lv ${agreement.status}`}>
                    {agreement.status === 'signed' ? 'Signed' : agreement.status === 'pending' ? 'Awaiting Signature' : 'Expired'}
                  </span>
                </div>

                {selectedAgreement?.id === agreement.id && agreement.status === 'pending' && (
                  <div className="agreement-details">
                    <div className="sign-section">
                      <input
                        type="text"
                        placeholder="Type your full legal name to sign"
                        value={signatureName}
                        onChange={(e) => setSignatureName(e.target.value)}
                        className="signature-input"
                      />
                      <button
                        className="action-btn-lv sign"
                        onClick={() => handleSignAgreement(agreement.id)}
                        disabled={signingAgreement || !signatureName.trim()}
                      >
                        {signingAgreement ? <Loader2 size={16} className="spinner" /> : <PenTool size={16} />}
                        Sign Agreement
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Signing Overlay Modal ── */}
      {signingDoc && (
        <>
          <div className="signing-overlay-backdrop" data-testid="signing-backdrop" onClick={() => { setSigningDoc(null); setLegalName(''); }} />
          <div className="signing-overlay-modal" data-testid="signing-modal">
            <div className="signing-header">
              <h2>Sign Document</h2>
              <button className="close-viewer" onClick={() => { setSigningDoc(null); setLegalName(''); }}>
                <X size={20} />
              </button>
            </div>
            <div className="signing-doc-title">
              <FileText size={20} />
              <span>{signingDoc.title}</span>
            </div>

            <div className="signing-form">
              <div className="sign-field">
                <label>Your Legal Full Name</label>
                <input
                  type="text"
                  value={legalName}
                  onChange={e => setLegalName(e.target.value)}
                  placeholder="Enter your full legal name"
                  data-testid="legal-name-input"
                />
              </div>

              <div className="sign-field">
                <label>Draw Your Signature</label>
                <div className="signature-pad-wrapper" ref={sigPadWrapperRef} data-testid="signature-pad">
                  <SignatureCanvas
                    ref={sigPadRef}
                    penColor="#000"
                    canvasProps={{
                      className: 'signature-canvas',
                      width: sigPadWidth,
                      height: Math.max(180, Math.round(sigPadWidth * 0.35)),
                    }}
                  />
                  <button
                    className="clear-sig"
                    onClick={() => sigPadRef.current?.clear()}
                    type="button"
                  >
                    <RotateCcw size={14} /> Clear
                  </button>
                </div>
              </div>

              <p className="signing-disclaimer">
                By clicking "Sign Document" below, you agree that your electronic signature is
                the legal equivalent of your handwritten signature. Your IP address and timestamp
                will be recorded for verification purposes.
              </p>

              <div className="signing-actions">
                <button className="cancel-btn" onClick={() => { setSigningDoc(null); setLegalName(''); }}>
                  Cancel
                </button>
                <button
                  className="sign-btn"
                  onClick={handleSign}
                  disabled={signing || !legalName.trim()}
                  data-testid="confirm-sign"
                >
                  {signing ? <Loader2 size={16} className="spinner" /> : <PenTool size={16} />}
                  {signing ? 'Signing...' : 'Sign Document'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LegalVault;
